import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

export interface HeroImage {
  file_id: string;
  caption?: string;
  /** present on public GETs — the ready-to-use image URL */
  url?: string;
}

export interface SiteSettings {
  hero: {
    locations: string[];
    headline_line1: string;
    headline_line2: string;
    description: string;
    taglines: string[];
    listen_now_url: string;
    images: HeroImage[];
    /** present on public GETs — resolved {file_id,url,caption} per visible image */
    image_urls?: HeroImage[];
    slideshow_interval_ms?: number;
  };
  about: { title: string; body: string; features: { line1: string; line2: string }[] };
  links: { spotify_artist_url: string; all_releases_url: string; contact_email: string };
  platforms: Record<string, string>;
  socials: Record<string, string>;
  footer: { copyright: string };
}

export interface ApiTrack {
  id: string;
  slug?: string | null;
  title: string;
  artist: string;
  album: string;
  genre: string;
  year: string;
  bpm: string;
  musical_key: string;
  label: string;
  description: string;
  external_url: string;
  audio_file_id?: string | null;
  cover_file_id?: string | null;
  audio_url?: string | null;
  cover_url?: string | null;
  audio_filename?: string;
  duration?: number | null;
  waveform?: number[] | null;
  preview_start?: number | null;
  featured: boolean;
  order: number;
  updated_at?: string;
}

export interface ApiRelease {
  id: string;
  slug?: string | null;
  title: string;
  artist: string;
  type: string;
  year: string;
  description: string;
  spotify_url: string;
  embed_url: string;
  cover_file_id?: string | null;
  cover_url?: string | null;
  track_ids?: string[];
  /** resolved tracklist (public content only) */
  tracks?: ApiTrack[];
  visible: boolean;
  order: number;
  updated_at?: string;
}

export interface ApiVideo {
  id: string;
  title: string;
  artist: string;
  type: string;
  year: string;
  album: string;
  description: string;
  url: string;
  provider: '' | 'youtube' | 'vimeo' | 'dailymotion' | 'file' | 'other' | 'upload';
  embed_url?: string | null;
  video_file_id?: string | null;
  video_url?: string | null;
  video_filename?: string;
  video_content_type?: string | null;
  thumbnail_file_id?: string | null;
  thumbnail_url?: string | null;
  source: 'upload' | 'link' | 'none';
  visible: boolean;
  views?: number;
  views_7d?: number;
  alert_sent_at?: string | null;
  alert_campaign_id?: string | null;
  order: number;
  updated_at?: string;
}

export interface SiteContent {
  settings: SiteSettings;
  tracks: ApiTrack[];
  releases: ApiRelease[];
  all_releases: ApiRelease[];
  videos?: ApiVideo[];
}

/** Defaults = the approved reference design (used until / if the API is unreachable). */
export const DEFAULT_SETTINGS: SiteSettings = {
  hero: {
    locations: ['SAN ANTONIO', 'HAVANA', 'ECUADOR'],
    headline_line1: 'House. Tech House.',
    headline_line2: 'Latin Soul.',
    description:
      'The Ekuba Project is a boundary-pushing electronic music experience by Ernesto Couso, blending house grooves, tech-house pulses, and Latin melodic textures.',
    taglines: ['RHYTHM', 'CULTURE', 'CONNECTION', 'HIGHER FREQUENCIES'],
    listen_now_url: '',
    images: [],
    image_urls: [],
    slideshow_interval_ms: 6000,
  },
  about: {
    title: 'ABOUT THE PROJECT',
    body: 'The Ekuba Project is the electronic music project of Ernesto Couso, based in San Antonio, Texas, with deep roots in Havana, Cuba and Ecuador. Since 2010, the project has explored the fusion of house and tech-house with Latin instruments and melodies, creating a unique sound that connects cultures, dance floors and people.',
    features: [
      { line1: 'ELECTRONIC', line2: 'ROOTS' },
      { line1: 'LATIN', line2: 'INFLUENCES' },
      { line1: 'GLOBAL', line2: 'PERSPECTIVE' },
    ],
  },
  links: { spotify_artist_url: '', all_releases_url: '', contact_email: '' },
  platforms: { spotify: '', 'apple-music': '', 'amazon-music': '', soundcloud: '', bandcamp: '' },
  socials: { instagram: '', facebook: '', youtube: '', soundcloud: '', x: '' },
  footer: { copyright: '© 2024 The Ekuba Project. All rights reserved.' },
};

const mk = (slug: string, title: string, duration: number, order: number): ApiTrack => ({
  id: slug, slug, title, artist: 'The Ekuba Project', album: '', genre: '', year: '', bpm: '', musical_key: '', label: '',
  description: '', external_url: '', cover_url: `/assets/tracks/${slug}.jpg`, duration, featured: true, order,
});
const mr = (slug: string, title: string, type: string, year: string, order: number): ApiRelease => ({
  id: slug, slug, title, artist: 'The Ekuba Project', type, year, description: '', spotify_url: '', embed_url: '',
  cover_url: `/assets/releases/${slug}.jpg`, visible: true, order,
});

export const DEFAULT_CONTENT: SiteContent = {
  settings: DEFAULT_SETTINGS,
  tracks: [mk('asere', 'Asere', 384, 0), mk('electronic-inspiration', 'Electronic Inspiration', 432, 1), mk('french-kiss', 'French Kiss', 368, 2)],
  releases: [
    mr('midnight-asphalt', 'Midnight Asphalt', 'Album', '2024', 0),
    mr('ekubastic', 'Ekubastic', 'Album', '2023', 1),
    mr('same-old-bullshit', 'Same Old Bullshit', 'Single', '2023', 2),
    mr('back-to-basics', 'Back To Basics EP', 'EP', '2022', 3),
    mr('a-few-words', 'A Few Words', 'Single', '2021', 4),
  ],
  all_releases: [],
};
DEFAULT_CONTENT.all_releases = DEFAULT_CONTENT.releases;

export function useSiteContent() {
  const [content, setContent] = useState<SiteContent>(DEFAULT_CONTENT);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    api
      .get<SiteContent>('/public/content')
      .then((r) => {
        if (alive && r.data?.settings) setContent(r.data);
      })
      .catch(() => {
        /* keep reference defaults */
      })
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);
  return { content, loaded };
}
